import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Gantt, TaskModel } from '@bryntum/gantt';

import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useGanttSettingsStore } from 'store/ganttSettings.store';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { ganttConfig, projectConfig } from 'components/common/bryntumGantt/GanttConfig';
import { CustomGantt } from 'components/common/bryntumGantt/gantt';
import { taskTooltipFeature } from 'components/common/bryntumGantt/ganttFeatures';
import { serializeBryntumGanttDependencies } from 'components/common/bryntumGantt/helper';

import { Radio } from 'components/DesignSystem/Radio';
import { Select } from 'components/DesignSystem/Select';
import { Switch } from 'components/DesignSystem/Switch';
import { ProgressType, getFlatTasks, groupViewList } from 'components/widgets/Gantt/components/GanttChartDisplay.utils';
import { IActivitiesSummaryResponse, IGanttChartResponse } from 'components/widgets/Gantt/components/ganttChart.types';
import classes from 'components/widgets/Gantt/components/ganttChartDisplay.module.scss';
import { GanttSummary } from 'components/widgets/Gantt/components/ganttSummary';

interface Props {
    summaryData: IActivitiesSummaryResponse | undefined;
    ganttData: IGanttChartResponse | undefined;
    isLoading: boolean;
    isFullScreen: boolean;
    getInstanceAgain: (gantt: any) => void;
    widgetId?: string;
    progress: ProgressType;
    setProgress: React.Dispatch<React.SetStateAction<string>>;
    isCriticalPath: boolean;
    setIsCriticalPath: React.Dispatch<React.SetStateAction<boolean>>;
    isWbs: boolean;
    setIsWbs: React.Dispatch<React.SetStateAction<boolean>>;
    milestoneFragnet?: string;
    isMilestone: boolean;
    setIsMilestone: React.Dispatch<React.SetStateAction<boolean>>;
}

const GanttChartDisplay = ({
    isFullScreen,
    summaryData,
    ganttData,
    isLoading,
    getInstanceAgain,
    widgetId,
    progress,
    setProgress,
    isCriticalPath,
    setIsCriticalPath,
    isWbs,
    setIsWbs,
    milestoneFragnet,
    isMilestone,
    setIsMilestone,
}: Props) => {
    const { calendarViewRange, showActivityLabels, showProgressLine, showBaseline, showDependencies } =
        useGanttSettingsStore();

    const ganttInstance = useRef<Gantt | null>(null);

    const getInstance = useCallback((gantt) => {
        if (gantt) {
            ganttInstance.current = gantt;
            getInstanceAgain(gantt);
        }
    }, []);

    const { sendEventWithDimensions } = useEventWithDimensions();
    const [view, setView] = useState<ISelectOption<string>>(
        groupViewList.find((item) => item.value === 'expandAll') as ISelectOption<string>,
    );

    const { versionDate, flatTasks, dependencies } = useMemo(
        () => ({
            versionDate: ganttData?.versionDate,
            flatTasks: getFlatTasks({ activities: ganttData?.activities, progress }),
            dependencies: showDependencies ? serializeBryntumGanttDependencies(ganttData?.activities) : [],
        }),
        [ganttData, showDependencies],
    );

    const selectProgress = (event) => {
        const selection = event.target.value;
        sendEventWithDimensions({
            category: 'Gantt',
            action: 'radio selection',
            label: selection,
        });
        const instance = ganttInstance.current;
        if (instance) {
            const store = instance.project.taskStore;
            store.forEach((item) => {
                if (selection === 'criticality') {
                    item.percentDone = item.percentageCriticalityComplete;
                } else {
                    item.percentDone = item.pComp;
                }
            });
            setProgress(selection);
        }
    };

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.features.labels.disabled = !showActivityLabels;
        }
    }, [showActivityLabels]);

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.features.progressLine.disabled = !showProgressLine;
        }
    }, [showProgressLine]);

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.features.baselines.disabled = !showBaseline;
        }
    }, [showBaseline]);

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.viewPreset = calendarViewRange;
        }
    }, [calendarViewRange]);

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance && instance.project) {
            const store = instance.project.taskStore;
            if (isCriticalPath) {
                store.filter({
                    id: 'criticalPathFilter',
                    filterBy: (record) => record.criticalPath === true,
                });
            } else {
                store.removeFilter('criticalPathFilter');
            }
        }
    }, [isCriticalPath, flatTasks]);

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance && instance.project) {
            const store = instance.project.taskStore;
            if (isWbs) {
                store.filter({
                    id: 'wbsFilter',
                    filterBy: (record) => record.type === 'WBS',
                });
            } else {
                store.removeFilter('wbsFilter');
            }
        }
    }, [isWbs, flatTasks]);

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance && instance.project) {
            const store = instance.project.taskStore;
            if (isMilestone) {
                store.filter({
                    id: 'milestoneFilter',
                    filterBy: (record) => record.type === 'START_MILESTONE' || record.type === 'FINISH_MILESTONE',
                });
            } else {
                store.removeFilter('milestoneFilter');
            }
        }
    }, [isMilestone, flatTasks]);

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance && instance.project && flatTasks) {
            const taskIdByTaskVersionHashCode = flatTasks.find((item) => item.taskVersionHashCode === milestoneFragnet);

            if (taskIdByTaskVersionHashCode) {
                const record = instance.taskStore.getById(taskIdByTaskVersionHashCode.id);
                instance
                    .scrollTaskIntoView(record as TaskModel, {
                        block: 'center',
                        edgeOffset: 20,
                        animate: true,
                        highlight: true,
                    })
                    .then(() => instance.selectRow(record));
            }
        }
    }, [milestoneFragnet, flatTasks]);

    const handleStatus = (item: ISelectOption<string> | null) => {
        const action = item?.value;
        const instance = ganttInstance.current;

        if (instance && action) {
            const store = instance.project.taskStore;
            switch (action) {
                case 'expandAll':
                    instance.expandAll();
                    break;
                case 'collapseAll':
                    instance.collapseAll();
                    break;
                case 'default':
                    instance.collapseAll().then(() => {
                        store.forEach((item) => {
                            if (item.childLevel < 3) {
                                instance.expandTo(item.id);
                            }
                        });
                    });
                    break;
            }
            setView(item);
        }
    };

    const controlProps = (item: string) => ({
        checked: progress === item,
        onChange: selectProgress,
        value: item,
        name: 'gantt-progress',
    });

    const zoomIn = () => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.zoomIn();
        }
    };

    const zoomOut = () => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.zoomOut();
        }
    };

    return (
        <div className={classes.ganttChartDisplayContainer}>
            <div className={classes.progressSelection}>
                <div className={classes.radioContainer}>
                    {!widgetId && (
                        <div className={classes.progress}>
                            <span>Progress</span>

                            <Radio {...controlProps('criticality')} label={'Criticality % Complete'} />

                            <Radio {...controlProps('submittedDuration')} label={'Submitted % Complete'} />
                        </div>
                    )}
                    <div className={classes.selectView}>
                        <Select<ISelectOption<string>>
                            value={view}
                            options={groupViewList}
                            onChange={(value) => handleStatus(value)}
                        />
                    </div>
                    {!widgetId && (
                        <div className={classes.critical}>
                            <Switch
                                onChange={(e) => setIsCriticalPath(e.target.checked)}
                                checked={isCriticalPath}
                                label={'Critical Path Only'}
                                labelPlacement={'start'}
                            />
                            <Switch
                                onChange={(e) => setIsWbs(e.target.checked)}
                                checked={isWbs}
                                label={'WBS Only'}
                                labelPlacement={'start'}
                            />

                            <Switch
                                onChange={(e) => setIsMilestone(e.target.checked)}
                                checked={isMilestone}
                                label={'Milestones Only'}
                                labelPlacement={'start'}
                            />
                        </div>
                    )}

                    <MuiIcon icon={'zoom_in'} fontSize={'2.4rem'} onClick={zoomIn} color={'#7489aa'} />
                    <MuiIcon icon={'zoom_out'} fontSize={'2.4rem'} onClick={zoomOut} color={'#7489aa'} />
                </div>
                <GanttSummary data={summaryData} />
            </div>
            <div className={`${classes.container} ${isFullScreen ? classes.fullScreen : ''}`}>
                {isLoading && (
                    <div className={classes.spinnerContainer}>
                        <OverlayWithSpinner />
                    </div>
                )}

                {flatTasks && flatTasks?.length > 0 && versionDate && (
                    <CustomGantt
                        versionDate={versionDate}
                        getInstance={getInstance}
                        flatTasks={flatTasks}
                        dependencies={dependencies}
                        ganttConfig={ganttConfig}
                        projectConfig={projectConfig}
                        taskTooltipFeature={taskTooltipFeature}
                    />
                )}

                {!isLoading && flatTasks && flatTasks?.length === 0 && <NoData />}
            </div>
        </div>
    );
};

export default React.memo(GanttChartDisplay);
